"use client";

import type {
  PublicStoreCampaign,
  PublicCampaignCompany,
} from "@/types/public-campaign";

type Props = {
  campaign: PublicStoreCampaign | null | undefined;
  onProducts?: (ids: number[]) => void;
  onLead?: () => void;
  className?: string;
};

function partnerUrl(company: PublicCampaignCompany): string {
  if (company.custom_domain_active) {
    const hostname = company.custom_domain_active
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/\/$/, "");
    return `https://${hostname}`;
  }

  const configuredApi = process.env.NEXT_PUBLIC_API_URL ?? "";

  const isDev =
    configuredApi.includes("api-dev.fornecedor.net") ||
    (
      typeof window !== "undefined" &&
      (
        window.location.hostname === "dev.fornecedor.net" ||
        window.location.hostname.endsWith(".dev.fornecedor.net") ||
        window.location.hostname === "localhost"
      )
    );

  const platformDomain = isDev
    ? "dev.fornecedor.net"
    : "fornecedor.net";

  if (company.custom_subdomain_active) {
    return `https://${company.slug}.${platformDomain}`;
  }

  return `https://${platformDomain}/${encodeURIComponent(company.slug)}`;
}

export function campaignAction(
  campaign: PublicStoreCampaign,
  onProducts?: (ids: number[]) => void,
  onLead?: () => void,
): { href?: string; onClick?: () => void; external?: boolean } {
  if (
    campaign.destination_type === "internal_company" &&
    campaign.destination_company
  ) {
    return {
      href: partnerUrl(campaign.destination_company),
      external: true,
    };
  }

  if (
    campaign.destination_type === "external_url" &&
    campaign.external_url
  ) {
    try {
      const url = new URL(campaign.external_url);
      if (url.protocol === "https:") {
        return { href: url.href, external: true };
      }
    } catch {
      return {};
    }
  }

  if (
    campaign.destination_type === "participating_products" &&
    campaign.product_ids.length &&
    onProducts
  ) {
    return {
      onClick: () => onProducts(campaign.product_ids),
    };
  }

  if (campaign.destination_type === "lead_form" && onLead) {
    return { onClick: onLead };
  }

  return {};
}

export default function StorefrontAdBanner({
  campaign,
  onProducts,
  onLead,
  className = "",
}: Props) {
  if (!campaign?.desktop_art_url) return null;

  const action = campaignAction(campaign, onProducts, onLead);

  const artwork = (
    <picture>
      {campaign.mobile_art_url ? (
        <source
          media="(max-width: 639px)"
          srcSet={campaign.mobile_art_url}
        />
      ) : null}
      <img
        src={campaign.desktop_art_url}
        alt={campaign.title}
        loading="lazy"
        decoding="async"
        className="block h-auto max-h-[470px] w-full object-contain"
      />
    </picture>
  );

  const content = action.href ? (
    <a
      href={action.href}
      rel={action.external ? "noopener noreferrer sponsored" : undefined}
      className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600"
      aria-label={campaign.action_label || campaign.title}
    >
      {artwork}
    </a>
  ) : action.onClick ? (
    <button
      type="button"
      onClick={action.onClick}
      className="block w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600"
      aria-label={campaign.action_label || campaign.title}
    >
      {artwork}
    </button>
  ) : (
    artwork
  );

  return (
    <section
      aria-label={`Publicidade: ${campaign.title}`}
      className={className}
    >
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
        {content}
      </div>
    </section>
  );
}
